import React, { useEffect, useState } from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, Divider,
  Select, MenuItem, TextField, FormControl, InputLabel, Grid, Avatar, Chip
} from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import Co2Icon from '@mui/icons-material/Co2';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import SensorsIcon from '@mui/icons-material/Sensors';
import AutomationIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from './axiosConfig';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import AlertSnackbar from './AlertSnackbar';

const AutomationConfigPage = () => {
  const [relays, setRelays] = useState([]);
  const [pendingSensors, setPendingSensors] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const projectId = localStorage.getItem('projectId');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [roomOptions, setRoomOptions] = useState([]);

  useEffect(() => {
    fetchRelays();
    fetchPendingSensors();
    fetchRooms();
  }, [projectId]);

  const fetchRelays = async () => {
    try {
      const response = await axiosInstance.get(`/api/projects/${projectId}/relays`);
      setRelays(response.data);
    } catch (error) {
      console.error('Error fetching relays:', error);
    }
  };

  const fetchPendingSensors = async () => {
    try {
      const response = await axiosInstance.get('/api/pending-sensors');
      setPendingSensors(response.data);
    } catch (error) {
      console.error('Error fetching pending sensors:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/projects/${projectId}/rooms`);
      setRoomOptions(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const associateSensor = async (sensorId, roomId) => {
    try {
      await axiosInstance.post(`/api/register-sensor`, {
        sensorId,
        projectId,
        roomId
      });
      fetchPendingSensors();
      showSnackbar('Sensor asociado correctamente', 'success');
    } catch (error) {
      console.error('Error associating sensor:', error);
      showSnackbar('Error al asociar el sensor', 'error');
    }
  };

  const saveRelayConfig = async (relayId, config) => {
    try {
      await axiosInstance.put(`/api/relay/${relayId}/config`, config);
      fetchRelays();
      showSnackbar('Configuración guardada exitosamente', 'success');
    } catch (error) {
      showSnackbar('Error al guardar la configuración', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSwitchConfigChange = (relayId, switchIndex, field, value) => {
    setRelays(prevRelays =>
      prevRelays.map(relay =>
        relay.id === relayId
          ? {
              ...relay,
              switches: relay.switches.map((sw, index) =>
                index === switchIndex ? { ...sw, [field]: value } : sw
              ),
            }
          : relay
      )
    );
  };

  // Calculate statistics
  const totalRelays = relays.length;
  const activeRelays = relays.filter(relay => relay.status === 'active').length;
  const totalSensors = pendingSensors.length;
  const configuredRooms = roomOptions.length;

  return (
    <ModernLayout title="Configuración de Automatización">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Configuración de Automatización
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona relés, sensores y automatización de tu proyecto
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Relés Totales"
            value={totalRelays.toString()}
            subtitle="Dispositivos de control"
            icon={<SettingsIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Relés Activos"
            value={activeRelays.toString()}
            subtitle="Dispositivos operativos"
            icon={<AutomationIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Sensores Pendientes"
            value={totalSensors.toString()}
            subtitle="Esperando configuración"
            icon={<SensorsIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Rooms Configuradas"
            value={configuredRooms.toString()}
            subtitle="Espacios de cultivo"
            icon={<ThermostatIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Configuración de Relés */}
        <Grid item xs={12} lg={8}>
          <ModernCard
            title="Configuración de Relés y Automatización"
            subtitle="Gestiona los dispositivos de control automático"
          >
            <Box sx={{ p: 2 }}>
              {relays.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No hay relés configurados en este proyecto.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Los relés aparecerán aquí una vez que se configuren en las rooms.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {relays.map((relay, relayIndex) => (
                    <ListItem key={relay.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                      <Box
                        sx={{
                          width: '100%',
                          p: 2,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            }}
                          >
                            <SettingsIcon />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {relay.relay_name || `Relé ${relayIndex + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Room: {relay.room_name || 'N/A'}
                            </Typography>
                          </Box>
                          <Chip
                            label={relay.status || 'Inactivo'}
                            color={relay.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {relay.switches && relay.switches.map((sw, switchIndex) => (
                          <Box key={switchIndex} sx={{ width: '100%', mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                              Switch {switchIndex + 1}
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                  <InputLabel>Modo de Operación</InputLabel>
                                  <Select
                                    value={sw.mode || ''}
                                    onChange={e => handleSwitchConfigChange(relay.id, switchIndex, 'mode', e.target.value)}
                                    sx={{
                                      borderRadius: 2,
                                      background: 'rgba(255, 255, 255, 0.8)',
                                      backdropFilter: 'blur(10px)',
                                    }}
                                  >
                                    <MenuItem value="sensor">Automatización por Sensor</MenuItem>
                                    <MenuItem value="schedule">Programación por Horario</MenuItem>
                                    <MenuItem value="manual">Control Manual</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Umbral de Activación"
                                  type="number"
                                  value={sw.threshold || ''}
                                  onChange={e => handleSwitchConfigChange(relay.id, switchIndex, 'threshold', e.target.value)}
                                  fullWidth
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      background: 'rgba(255, 255, 255, 0.8)',
                                      backdropFilter: 'blur(10px)',
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <GradientButton
                            startIcon={<SaveIcon />}
                            onClick={() => saveRelayConfig(relay.id, relay.switches)}
                          >
                            Guardar Configuración de Relé
                          </GradientButton>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </ModernCard>
        </Grid>

        {/* Sensores Pendientes */}
        <Grid item xs={12} lg={4}>
          <ModernCard
            title="Sensores Pendientes"
            subtitle="Configura nuevos sensores detectados"
          >
            <Box sx={{ p: 2 }}>
              {pendingSensors.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No hay sensores pendientes de configuración.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {pendingSensors.map(sensor => (
                    <ListItem key={sensor.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          width: '100%',
                          p: 2,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            }}
                          >
                            <SensorsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Sensor ID: {sensor.identifier}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pendiente de asociación
                            </Typography>
                          </Box>
                        </Box>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Seleccionar Room</InputLabel>
                          <Select
                            value={selectedRoom}
                            onChange={(e) => setSelectedRoom(e.target.value)}
                            sx={{
                              borderRadius: 2,
                              background: 'rgba(255, 255, 255, 0.8)',
                              backdropFilter: 'blur(10px)',
                            }}
                          >
                            {roomOptions.map(room => (
                              <MenuItem key={room.id} value={room.id}>
                                {room.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <GradientButton
                          fullWidth
                          onClick={() => associateSensor(sensor.id, selectedRoom)}
                          disabled={!selectedRoom}
                          startIcon={<RefreshIcon />}
                        >
                          Asociar Sensor
                        </GradientButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </ModernCard>
        </Grid>
      </Grid>

      <AlertSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </ModernLayout>
  );
};

export default AutomationConfigPage;
